import { toast } from 'react-toastify';
import ArrowLoopDoodle from '@monofront/design-system/visuals/Illustrations/ArrowLoopDoodle';
import Modal from '@monofront/design-system/organisms/Modal';
import { useTranslations } from 'next-intl';
import { IShoppingCartProduct } from '@monofront/types';
import OOSModalCardProduct from './OOSModalCardProduct';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useEffect, useRef, useState } from 'react';
import { tryit } from 'radash';
import logger from '@utils/logger';
import { OOS_RECOMMENDATIONS_WIDGET_ID } from '@monofront/business/constants';
import { growthBookService } from '@services/businessContainer';

interface IModalReplaceOOSProductProps {
  isOpen: boolean;
  externalToggle: () => void;
  shoppingCartOOSProduct: IShoppingCartProduct;
}

const ModalReplaceOOSProduct: React.FC<IModalReplaceOOSProductProps> = ({
  isOpen,
  externalToggle,
  shoppingCartOOSProduct,
}) => {
  const tProducts = useTranslations('Products');
  const et = useTranslations('Errors');
  const replacementProduct = shoppingCartOOSProduct.replacementProduct!;
  const { decrementProductInShoppingCart, shoppingCartId } = useShoppingCart();
  const [isReplaced, setIsReplaced] = useState(false);

  const enteredRef = useRef(false);

  useEffect(() => {
    if (!isOpen || enteredRef.current) return;
    growthBookService.enterExperimentFromFeatureFlag(
      OOS_RECOMMENDATIONS_WIDGET_ID[0],
    );
    enteredRef.current = true;
  }, [isOpen]);

  const editCountCallback = async () => {
    try {
      if (!shoppingCartId) {
        throw new Error('Unexpected empty `shoppingCartId`');
      }

      if (!isReplaced) {
        const tryDecrementProductInShoppingCart = tryit(
          decrementProductInShoppingCart,
        );

        const [err] = await tryDecrementProductInShoppingCart(
          shoppingCartId,
          shoppingCartOOSProduct,
        );

        if (err) {
          throw new Error('Error removing product in shopping cart', {
            cause: err,
          });
        } else {
          setIsReplaced(true);
          toast(tProducts('oosReplaceSuccessNotification'));
        }
      }
    } catch (err: unknown) {
      toast.error(et('anErrorOccured'), {
        progressClassName: 'toast-error',
      });

      logger.error(err);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        externalToggle={() => {
          setIsReplaced(false);
          externalToggle();
        }}
        UNSAFE_className="modal-oos"
        hasPadding
      >
        <div className="container">
          <div className="title-and-card">
            <h3 className="accent-title initial-product">
              {tProducts('oosInitialProduct')}
            </h3>
            {/* TODO: mutualize products structure https://lafourche.atlassian.net/browse/LF-376 */}
            <OOSModalCardProduct product={shoppingCartOOSProduct} isDisabled />
          </div>
          <div className="arrow">
            <ArrowLoopDoodle width={300} />
          </div>
          <div className="title-and-card">
            <h3 className="accent-title replacement-product">
              {tProducts('oosReplacementProduct')}
            </h3>
            {/* TODO: mutualize products structure https://lafourche.atlassian.net/browse/LF-376 */}
            <OOSModalCardProduct
              product={replacementProduct}
              editCountCallback={editCountCallback}
            />
          </div>
        </div>
      </Modal>
      <style jsx global>{`
        .modal-oos {
          top: 25%;
          max-width: 634px;
          padding: 20px;
        }

        @media (max-width: 800px) {
          .modal-oos {
            top: 10%;
            width: 375px;
            padding: 0;
          }
        }
      `}</style>
      <style jsx>{`
        .container {
          display: flex;
          flex-direction: row;
        }

        .title-and-card {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        h3 {
          text-align: center;
          width: fit-content;
          margin-bottom: var(--space-4);
        }

        .initial-product {
          background-color: var(--clr-secondary-3);
        }

        .replacement-product {
          background-color: var(--clr-tertiary-2);
        }

        .arrow {
          display: flex;
          align-items: center;
          width: 100px;
          transform: rotate(180deg);
        }

        @media (max-width: 800px) {
          .container {
            flex-direction: column;
            align-items: center;
          }

          .title-and-card {
            width: 100%;
          }

          .arrow {
            transform: rotate(270deg);
            margin: var(--space-5) 0;
          }

          .accent-title {
            margin-bottom: var(--space-4);
            font-size: var(--font-size-1-5);
          }
        }
      `}</style>
    </>
  );
};

export default ModalReplaceOOSProduct;
